import React, { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "../lib/api";
import { useLanguage } from "../lib/LanguageContext";
import { gaConsultationSubmit, newEventId, metaMatchFields } from "../lib/analytics";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
    "Property Management",
    "Estate Oversight",
    "Concierge Services",
    "Financial Administration",
    "Luxury Rental Oversight",
    "Hurricane Preparation",
    "Maintenance & Construction",
];

const COMMUNITY_OPTIONS = [
    "Palmilla",
    "Villas del Mar",
    "Querencia",
    "Chileno Bay",
    "Maravilla",
    "El Dorado",
];

const initial = {
    name: "",
    email: "",
    phone: "",
    property_location: "",
    service_interest: "",
    message: "",
};

export default function ConsultationForm({ variant = "light" }) {
    const { t } = useLanguage();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const isDark = variant === "dark";

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // One shared id for this conversion — sent to both the browser Pixel
        // (via gaConsultationSubmit) and the server CAPI (via the payload).
        const eventId = newEventId();
        try {
            await apiClient.post("/inquiries", {
                ...form,
                event_id: eventId,
                event_source_url: typeof window !== "undefined" ? window.location.href : "",
                ...metaMatchFields(),
            });
            // GA4 conversion — categorical params only, zero PII.
            gaConsultationSubmit({
                service_interest: form.service_interest || "unspecified",
                property_location: form.property_location || "unspecified",
            }, eventId);
            setSuccess(true);
            setForm(initial);
            toast.success(t("form.successToast"));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || t("form.failToast"));
        } finally {
            setLoading(false);
        }
    };

    const labelCls = isDark ? "text-pearl/55" : "text-navy/55";
    const inputCls = isDark
        ? "w-full bg-transparent border-b border-pearl/25 px-0 py-3 text-pearl placeholder:text-pearl/40 focus:outline-none focus:border-gold transition-colors duration-300 text-sm tracking-wide"
        : "input-luxe";

    if (success) {
        return (
            <div className={`p-10 border ${isDark ? "border-pearl/20 text-pearl" : "border-navy/10 text-navy"} text-center`} data-testid="consultation-success">
                <p className="overline mb-4 text-gold">{t("form.successEyebrow")}</p>
                <h3 className="font-serif text-3xl mb-3">{t("form.successTitle")}</h3>
                <p className="text-sm opacity-80 max-w-md mx-auto leading-relaxed">{t("form.successBody")}</p>
                <button
                    onClick={() => setSuccess(false)}
                    data-testid="consultation-submit-another"
                    className={`mt-8 text-[0.7rem] uppercase tracking-[0.28em] underline underline-offset-4 ${isDark ? "text-gold" : "text-navy hover:text-gold"} transition-colors`}
                >
                    {t("form.sendAnother")}
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="consultation-form" className="space-y-7">
            <div className="grid sm:grid-cols-2 gap-7">
                <label className="block">
                    <span className={`overline ${labelCls}`}>{t("form.name")}</span>
                    <input required value={form.name} onChange={update("name")} data-testid="form-input-name" className={inputCls} placeholder={t("form.namePh")} />
                </label>
                <label className="block">
                    <span className={`overline ${labelCls}`}>{t("form.email")}</span>
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="form-input-email" className={inputCls} placeholder={t("form.emailPh")} />
                </label>
                <label className="block">
                    <span className={`overline ${labelCls}`}>{t("form.phone")}</span>
                    <input required value={form.phone} onChange={update("phone")} data-testid="form-input-phone" className={inputCls} placeholder={t("form.phonePh")} />
                </label>
                <label className="block">
                    <span className={`overline ${labelCls}`}>
                        {t("form.propertyLocation")}
                        <span className="ml-2 normal-case tracking-normal text-[0.65rem] opacity-60">({t("form.propertyLocationOptional")})</span>
                    </span>
                    <select value={form.property_location} onChange={update("property_location")} data-testid="form-select-property" className={`${inputCls} appearance-none cursor-pointer`}>
                        <option value="">{t("form.selectCommunity")}</option>
                        {COMMUNITY_OPTIONS.map((c) => (<option key={c} value={c}>{c}</option>))}
                        <option value="Not Sure Yet / Currently Evaluating">{t("form.notSure")}</option>
                        <option value="Other">{t("form.other")}</option>
                    </select>
                </label>
                <label className="block sm:col-span-2">
                    <span className={`overline ${labelCls}`}>{t("form.serviceInterest")}</span>
                    <select required value={form.service_interest} onChange={update("service_interest")} data-testid="form-select-service" className={`${inputCls} appearance-none cursor-pointer`}>
                        <option value="">{t("form.selectService")}</option>
                        {SERVICE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                        <option value="Other">{t("form.other")}</option>
                    </select>
                </label>
                <label className="block sm:col-span-2">
                    <span className={`overline ${labelCls}`}>{t("form.message")}</span>
                    <textarea required rows={4} value={form.message} onChange={update("message")} data-testid="form-input-message" className={`${inputCls} resize-none`} placeholder={t("form.messagePh")} />
                </label>
            </div>

            <button type="submit" disabled={loading} data-testid="form-submit-button" className={isDark ? "btn-luxe-light" : "btn-luxe-primary"}>
                {loading ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> {t("common.sending")}</>
                ) : (
                    <>{t("common.sendInquiry")}<Send className="w-3.5 h-3.5" strokeWidth={1.5} /></>
                )}
            </button>
        </form>
    );
}
